import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { usePublicAgent } from "@/hooks/agent/usePublicAgent";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MainLayout from "@/components/layout/MainLayout";
import PageHeader from "@/components/layout/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MessageCircle,
  Check,
  Clock,
  Globe,
  User,
  Copy,
  Code,
} from "lucide-react";

const PreviewChatBubble = () => {
  const { publicId } = useParams<{ publicId: string }>();

  // Function to mask the public ID for display
  const maskPublicId = (id: string | undefined) => {
    if (!id) return "Unknown";
    if (id.length <= 8) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
  };
  const { agent, organization, isLoading, error } = usePublicAgent(
    publicId,
    undefined,
  );
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate the embed script code
  const generateEmbedScript = () => {
    const currentOrigin = window.location.origin;
    return `<script
  type="text/javascript"
  async
  src="${currentOrigin}/embed.js"
  data-agent-id="${publicId}"
  data-type="bubble"
  data-welcome-message="${agent?.welcome_message || "Hi! How can I assist you?"}"
></script>`;
  };

  // Copy embed script to clipboard
  const copyEmbedScript = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedScript());
      setCopied(true);
      toast.success("Embed script copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  useEffect(() => {
    console.log(
      "PreviewChatBubble: Component mounted with publicId:",
      publicId,
    );
    console.log("PreviewChatBubble: Agent loading state:", isLoading);
    console.log("PreviewChatBubble: Agent data:", agent);
    console.log("PreviewChatBubble: Error:", error);
  }, [publicId, isLoading, agent, error]);

  useEffect(() => {
    if (!agent || isLoading || error) {
      console.log("PreviewChatBubble: Not loading script yet", {
        agent: !!agent,
        isLoading,
        error,
      });
      return;
    }

    console.log("PreviewChatBubble: Loading embed script for agent:", agent);

    // Enable debug mode for development
    window.hubfloDebug = true;

    // Create and inject the embed script for the preview
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `${window.location.origin}/embed.js`;
    script.dataset.agentId = publicId || "";
    script.dataset.type = "bubble";
    script.dataset.welcomeMessage =
      agent.welcome_message || "Hi! How can I assist you?";

    script.onload = () => {
      console.log("PreviewChatBubble: Embed script loaded successfully");
      setScriptLoaded(true);

      // Check for widget visibility after a short delay
      setTimeout(() => {
        // Check for multiple possible widget container classes
        const containers = [
          ".ai-chat-widget-container",
          ".hubflo-widget",
          ".chat-widget",
          "[data-widget-type='bubble']",
          ".widget-container",
        ];

        let foundContainer = null;
        for (const selector of containers) {
          foundContainer = document.querySelector(selector);
          if (foundContainer) {
            console.log(
              "PreviewChatBubble: Widget container found with selector:",
              selector,
            );
            break;
          }
        }

        if (foundContainer) {
          setWidgetVisible(true);
          console.log("PreviewChatBubble: Widget container found");
        } else {
          console.warn(
            "PreviewChatBubble: Widget container not found after script load",
          );
          // Log all elements to help debug
          console.log(
            "All elements:",
            document.querySelectorAll(
              '*[class*="widget"], *[class*="chat"], *[data-widget]',
            ),
          );
        }
      }, 1000);
    };

    script.onerror = (e) => {
      console.error("PreviewChatBubble: Failed to load embed script:", e);
    };

    document.body.appendChild(script);

    return () => {
      // Clean up the script when component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Also remove any widget elements that were created
      const container = document.querySelector(".ai-chat-widget-container");
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }

      setScriptLoaded(false);
      setWidgetVisible(false);
    };
  }, [agent, publicId, isLoading, error]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Simple Page Header Skeleton */}
          <div className="border-b bg-white px-4 py-6 md:px-6">
            <div className="container mx-auto max-w-6xl">
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
          </div>

          <div className="container mx-auto py-8 px-4 max-w-6xl flex-1 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
              {/* Simple How to Test Card */}
              <div className="lg:col-span-1 min-w-0">
                <Card className="border-2 border-blue-200 bg-blue-50/30 overflow-hidden">
                  <CardHeader>
                    <Skeleton className="h-5 w-24" />
                  </CardHeader>
                  <CardContent className="space-y-4 overflow-hidden">
                    <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 overflow-hidden">
                      <Skeleton className="h-4 w-32 mb-2" />
                      <Skeleton className="h-3 w-full mb-1" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>

                    <div className="space-y-3 overflow-hidden">
                      {[1, 2, 3].map((num) => (
                        <div
                          key={num}
                          className="flex items-start gap-3 min-w-0"
                        >
                          <Skeleton className="bg-blue-600 rounded-full w-6 h-6 flex-shrink-0" />
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <Skeleton className="h-3 w-20 mb-1" />
                            <Skeleton className="h-3 w-full max-w-32" />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 overflow-hidden">
                      <Skeleton className="h-4 w-24 mb-2" />
                      <div className="space-y-1 overflow-hidden">
                        <Skeleton className="h-3 w-full max-w-40" />
                        <Skeleton className="h-3 w-full max-w-36" />
                        <Skeleton className="h-3 w-full max-w-44" />
                        <Skeleton className="h-3 w-full max-w-38" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Simple Main Content */}
              <div className="lg:col-span-2 space-y-6 min-w-0">
                {/* Widget Status Card */}
                <Card className="overflow-hidden">
                  <CardHeader className="overflow-hidden">
                    <Skeleton className="h-5 w-28" />
                  </CardHeader>
                  <CardContent className="overflow-hidden">
                    <div className="grid grid-cols-2 gap-4 min-w-0">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-28" />
                    </div>
                  </CardContent>
                </Card>

                {/* Embed Script Card */}
                <Card className="overflow-hidden">
                  <CardHeader className="overflow-hidden">
                    <Skeleton className="h-5 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-4 overflow-hidden">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-hidden">
                      <Skeleton className="h-4 w-28 mb-2" />
                      <Skeleton className="h-3 w-full mb-1" />
                      <Skeleton className="h-3 w-2/3 mb-3" />

                      <div className="bg-gray-900 rounded-lg p-4 relative overflow-hidden">
                        <div className="space-y-1 overflow-hidden">
                          <Skeleton className="h-2 w-full bg-gray-700" />
                          <Skeleton className="h-2 w-3/4 bg-gray-700" />
                          <Skeleton className="h-2 w-1/2 bg-gray-700" />
                          <Skeleton className="h-2 w-2/3 bg-gray-700" />
                        </div>
                        <Skeleton className="absolute top-2 right-2 h-6 w-6" />
                      </div>

                      <Skeleton className="h-3 w-full max-w-48 mt-3" />
                    </div>
                  </CardContent>
                </Card>

                {/* Agent Information Card */}
                <Card className="overflow-hidden">
                  <CardHeader className="overflow-hidden">
                    <Skeleton className="h-5 w-36" />
                  </CardHeader>
                  <CardContent className="space-y-4 overflow-hidden">
                    <div className="space-y-2 overflow-hidden">
                      <div className="flex items-center justify-between min-w-0">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                      <div className="flex items-center justify-between min-w-0">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    </div>

                    <div className="pt-4 border-t overflow-hidden">
                      <Skeleton className="h-3 w-20 mb-2" />
                      <div className="flex items-center justify-between min-w-0">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>

                    <div className="pt-4 border-t overflow-hidden">
                      <Skeleton className="h-3 w-28 mb-2" />
                      <Skeleton className="h-3 w-full mb-1" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>

                    <div className="pt-4 border-t overflow-hidden">
                      <Skeleton className="h-3 w-16 mb-2" />
                      <Skeleton className="h-2 w-full max-w-32" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !agent) {
    return (
      <MainLayout>
        <PageHeader title="Chat Bubble Preview" />
        <div className="container mx-auto py-10 px-4 max-w-screen-lg">
          <Card className="shadow-md bg-white p-8">
            <Alert variant="destructive">
              <AlertDescription>
                <strong>Error loading agent:</strong>{" "}
                {error || "Agent not found"}
                <br />
                <br />
                <strong>Debug info:</strong>
                <br />• Public ID: {publicId}
                <br />• Agent data: {agent ? "Found" : "Not found"}
                <br />• Error: {error || "Unknown error"}
                <br />
                <br />
                Please check that:
                <br />
                1. The agent exists and is active
                <br />
                2. The public ID is correct
                <br />
                3. The agent is published/public
              </AlertDescription>
            </Alert>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full overflow-y-auto">
        <PageHeader
          title="Chat Widget Preview"
          subtitle="Test your chat widget exactly as it will appear on your website"
        />
        <div className="container mx-auto py-8 px-4 max-w-6xl flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* How to Test Card - Now prominently placed on the left */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <MessageCircle className="h-5 w-5" />
                    How to Test
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      💬 Chat Widget Preview
                    </h4>
                    <p className="text-blue-800 text-sm">
                      Look for the chat bubble in the bottom-right corner of
                      your screen. This is exactly how it will appear on your
                      website.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                        1
                      </div>
                      <div>
                        <h5 className="font-medium text-blue-900">
                          Click the Chat Bubble
                        </h5>
                        <p className="text-sm text-blue-700">
                          Click on the floating bubble to open the chat
                          interface
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                        2
                      </div>
                      <div>
                        <h5 className="font-medium text-blue-900">
                          Test the Conversation
                        </h5>
                        <p className="text-sm text-blue-700">
                          Send a message to see how your agent responds
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                        3
                      </div>
                      <div>
                        <h5 className="font-medium text-blue-900">
                          Check Mobile View
                        </h5>
                        <p className="text-sm text-blue-700">
                          Resize your browser or test on mobile to see
                          responsive behavior
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">
                      ✨ Key Features
                    </h4>
                    <ul className="text-green-800 text-sm space-y-1">
                      <li>• Customizable welcome message and branding</li>
                      <li>• Mobile-responsive design</li>
                      <li>• Smooth animations and transitions</li>
                      <li>• Real-time conversation handling</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Widget Status, Embed Script, and Agent Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Widget Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Widget Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      {scriptLoaded ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="text-sm">Script Loaded</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {widgetVisible ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="text-sm">Widget Visible</span>
                    </div>
                  </div>

                  {!widgetVisible && scriptLoaded && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertDescription>
                        The script loaded successfully but the widget is not
                        visible. Check the browser console for error messages.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Embed Script Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    Embed Script
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      📋 Copy & Paste
                    </h4>
                    <p className="text-gray-700 text-sm mb-3">
                      Copy this script and paste it into your website's HTML to
                      add the chat widget.
                    </p>

                    <div className="bg-gray-900 rounded-lg p-4 relative">
                      <pre className="text-green-400 text-xs overflow-x-auto whitespace-pre-wrap">
                        <code>{generateEmbedScript()}</code>
                      </pre>
                      <Button
                        onClick={copyEmbedScript}
                        size="sm"
                        variant="secondary"
                        className="absolute top-2 right-2 h-8 w-8 p-0"
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="mt-3 text-xs text-gray-600">
                      <strong>Installation:</strong> Place this script before
                      the closing &lt;/body&gt; tag in your HTML.
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Agent Information Card - Moved to right side */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Agent Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Name:</span>
                      <span className="text-sm text-gray-600">
                        {agent?.agent_name || "Unknown"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Status:</span>
                      <Badge
                        variant={agent?.is_active ? "default" : "secondary"}
                      >
                        {agent?.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-2">Organization</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Name:</span>
                        <span className="text-sm text-gray-600">
                          {organization?.name || "Unknown"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-2">
                      Welcome Message
                    </h4>
                    <p className="text-sm text-gray-600 italic">
                      "{agent?.welcome_message || "Hi! How can I assist you?"}"
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium mb-2">Agent ID</h4>
                    <p className="text-xs text-gray-500 font-mono break-all">
                      {maskPublicId(publicId)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PreviewChatBubble;
